import {
  readAccessMembers,
  revokeAccessMember,
  updateAccessMember,
  type AccessMember,
} from '@/api/core/access-members';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import { Button, Input, ListPage, PageHeader } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { propertyRuntime, type PropertyRuntime } from '../property/property-queries';
/** 투숙에서 생성된 출입 권한은 지점과 호실로 조회한다. */
export function ResidentAccessPage({ runtime = propertyRuntime }: { runtime?: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  return <ResidentAccessContent key={session.generation} runtime={runtime} />;
}

function ResidentAccessContent({ runtime }: { runtime: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  const [property, setProperty] = useState('');
  const [room, setRoom] = useState('');
  const [filter, setFilter] = useState({ property: '', room: '' });
  const [cursor, setCursor] = useState<string>();
  const [editing, setEditing] = useState<AccessMember>();
  const [revoking, setRevoking] = useState<AccessMember>();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const allowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 1);
  const writable =
    allowed && !!session.authorization && canReadDomain(session.authorization, 'entitlement', 4);
  const currentWrite = () => {
    const actor = runtime.session.getSnapshot();
    return (
      actor.generation === session.generation &&
      actor.status === 'authorized' &&
      !!actor.authorization &&
      !actor.deniedDomains.includes('entitlement') &&
      canReadDomain(actor.authorization, 'entitlement', 4)
    );
  };
  const mutate = async (member: AccessMember, action: 'update' | 'revoke') => {
    if (!writable || !currentWrite() || busy) return;
    const parseKst = (value: string) => {
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
      const date = new Date(`${value}:00+09:00`);
      return Number.isFinite(date.getTime()) ? date.toISOString() : null;
    };
    const from = parseKst(start),
      until = parseKst(end);
    if (action === 'update' && (!from || !until || from >= until)) {
      setMessage('권한 기간을 확인해 주세요.');
      return;
    }
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      const command = { command_id: crypto.randomUUID(), expected_revision: member.revision };
      if (action === 'update' && from && until)
        await updateAccessMember(runtime.client, member.id, {
          ...command,
          valid_from: from,
          valid_until: until,
        });
      else await revokeAccessMember(runtime.client, member.id, command);
      if (!currentWrite()) return;
      setEditing(undefined);
      setRevoking(undefined);
      setMessage(action === 'update' ? '기간을 저장했습니다.' : '멤버를 회수했습니다.');
      await list.refetch();
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '멤버가 변경되었습니다. 다시 조회해 주세요.'
          : '멤버를 변경하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  const list = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'resident-access'),
      filter.property,
      filter.room,
      cursor ?? '',
    ],
    queryFn: ({ signal }) =>
      readAccessMembers(
        runtime.client,
        {
          source_kind: 'STAY',
          ...(filter.property ? { property_id: filter.property } : {}),
          ...(filter.room ? { room_id: filter.room } : {}),
          ...(cursor ? { after: cursor } : {}),
        },
        signal,
      ),
    enabled: allowed,
  });
  if (session.status !== 'authorized') return null;
  return (
    <ListPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader title="계약자 출입" />
      </ShellHeader>
      <div className="space-y-4 p-4">
        <label>
          지점 UUID
          <Input
            value={property}
            onChange={(e) => {
              setProperty(e.target.value);
            }}
          />
        </label>
        <label>
          호실 UUID
          <Input
            value={room}
            onChange={(e) => {
              setRoom(e.target.value);
            }}
          />
        </label>
        <Button
          disabled={(!!property && !isUuid(property)) || (!!room && !isUuid(room))}
          onClick={() => {
            setFilter({ property, room });
            setCursor(undefined);
          }}
        >
          조회
        </Button>
        {allowed ? null : <p role="alert">출입 권한을 조회할 수 없습니다.</p>}
        {list.isError ? <p role="alert">계약자 출입을 불러오지 못했습니다.</p> : null}
        {allowed && list.data ? (
          <>
            <ul>
              {list.data.items.map((m) => (
                <li key={m.id}>
                  {m.name ?? m.user_id ?? '비회원'} · {m.phone_masked ?? '—'} · {m.valid_from} ~{' '}
                  {m.valid_until}
                  {m.revoked_at ? ' · 회수됨' : ''}
                  {writable && !m.revoked_at ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditing(m);
                          setStart('');
                          setEnd('');
                        }}
                      >
                        기간 수정
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setRevoking(m);
                        }}
                      >
                        회수
                      </Button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
            {list.data.next_cursor ? (
              <Button
                onClick={() => {
                  setCursor(list.data.next_cursor ?? undefined);
                }}
              >
                다음
              </Button>
            ) : null}
          </>
        ) : null}
        {editing && writable ? (
          <section aria-label="계약자 기간 수정">
            <label>
              권한 시작 KST
              <Input
                type="datetime-local"
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                }}
              />
            </label>
            <label>
              권한 종료 KST
              <Input
                type="datetime-local"
                value={end}
                onChange={(e) => {
                  setEnd(e.target.value);
                }}
              />
            </label>
            <Button disabled={busy} onClick={() => void mutate(editing, 'update')}>
              기간 저장
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setEditing(undefined);
              }}
            >
              취소
            </Button>
          </section>
        ) : null}
        {revoking && writable ? (
          <section role="alert">
            <p>선택한 계약자 권한을 회수할까요?</p>
            <Button disabled={busy} onClick={() => void mutate(revoking, 'revoke')}>
              회수 확인
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setRevoking(undefined);
              }}
            >
              취소
            </Button>
          </section>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
      </div>
    </ListPage>
  );
}
